<script setup lang="ts">
  import type { PropType } from 'vue';
  import type { Package } from '@/models/package';
  import { useAppStore } from '@/stores/app-store';

  const props = defineProps({
    package: {
      type: Object as PropType<Package>,
      required: true
    },
    localePath: {
      type: Function as PropType<(to: any) => string>,
      required: true
    },
    useCaseSlug: {
      type: String,
      required: true
    }
  });

  const emit = defineEmits(['onSelect']);

  const { beginFadeNavigation } = useAppStore();

  const onPackageSelect = async (packageTitle: string) => {
    if (props.package.customText?.length) {
      beginFadeNavigation({ path: props.localePath('custom-package'), query: { slug: props.useCaseSlug } });
      return;
    }

    emit('onSelect', packageTitle);

    const element = document.getElementById('contact-section');

    if (element) {
      element.scrollIntoView();
    }
  };
</script>

<template>
  <div class="use-case-package-card">
    <div class="use-case-package-card__title">
      <h2>{{ package.title }}</h2>
      <p>{{ package.description }}</p>
    </div>
    <div class="use-case-package-card__body">
      <div>
        <div class="use-case-package-card__properties">
          <div
            v-for="(packageItem, index) in package.items"
            :key="index"
            :class="['use-case-package-property', { 'use-case-package-property--check-only': !packageItem.value }]">
            <div
              v-if="packageItem.name"
              class="use-case-package-property__name">
              {{ packageItem.name }}
            </div>
            <div v-if="packageItem.value" class="use-case-package-property__tag">
              <!--<div class="use-case-package-property__check" />-->
              <span v-if="packageItem.value">{{ packageItem.value }}</span>
            </div>
          </div>
        </div>
        <div v-if="package.interactionModes && package.interactionModes.length" class="use-case-package-card__interaction-modes">
          <div class="use-case-package-card__subtitle">
            {{ $t('package.interactionModesTitle') }}
          </div>
          <div
            v-for="(packageItem, index) in package.interactionModes"
            :key="index"
            class="use-case-package-interaction-mode-item">
            <div
              v-if="packageItem.name"
              class="use-case-package-interaction-mode-item__name">
              {{ packageItem.name }}
            </div>
            <div class="use-case-package-interaction-mode-item__tag">
              <span v-if="packageItem.value">{{ packageItem.value }}</span>
            </div>
          </div>
        </div>
        <div v-if="package.otherFeatures && package.otherFeatures.length" class="use-case-package-card__other-features">
          <div class="use-case-package-card__subtitle">
            {{ $t('package.otherFeaturesTitle') }}
          </div>
          <div
            v-for="(packageItem, index) in package.otherFeatures"
            :key="index"
            class="use-case-package-other-feature-item">
            <div class="use-case-package-other-feature-item__tag">
              <span v-if="packageItem.value">{{ packageItem.value }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="package.customText"
          class="use-case-package-card__info">
          {{ package.customText }}
        </div>
      </div>
      <div :class="['use-case-package-card__bottom', { 'use-case-package-card__bottom--no-price': !package.price }]">
        <!--<div
          v-if="package.customText"
          class="use-case-package-card__info">
          {{ package.customText }}
        </div>-->
        <div
          v-if="package.price && package.priceType"
          class="use-case-package-card__net">
          {{ package.priceType }}
        </div>
        <div
          v-if="package.price"
          class="use-case-package-card__price">
          {{ package.price }}
          <span v-if="package.priceFrequency">/ {{ package.priceFrequency }}</span>
        </div>
        <a
          class="use-case-package-card__link"
          @click.prevent="onPackageSelect(package.title)">
          {{ package.buttonText }}
        </a>
      </div>
    </div>
  </div>
</template>
